import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import PhoneLink from './PhoneLink';
import { tenantService } from '../services/api';
import { TenantLead } from '../types';
import './LeadsPanel.css';

const emptyLeadForm = () => ({ name: '', phoneNumber: '', comments: '' });

function formatLeadDate(lead: TenantLead): string {
  const raw = lead.createdAt;
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

type LeadsPanelProps = {
  hostelId: number;
};

const LeadsPanel: React.FC<LeadsPanelProps> = ({ hostelId }) => {
  const [leads, setLeads] = useState<TenantLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<TenantLead | null>(null);
  const [leadForm, setLeadForm] = useState(emptyLeadForm);
  const [leadSaving, setLeadSaving] = useState(false);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tenantService.getLeadsByHostel(hostelId);
      setLeads(data);
    } catch {
      toast.error('Failed to load leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [hostelId]);

  useEffect(() => {
    void loadLeads();
  }, [loadLeads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const hay = [l.name, l.phoneNumber, l.comments].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [leads, query]);

  useEffect(() => {
    setPage(1);
  }, [query, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const openAddLead = () => {
    setEditingLead(null);
    setLeadForm(emptyLeadForm());
    setLeadModalOpen(true);
  };

  const openEditLead = (lead: TenantLead) => {
    setEditingLead(lead);
    setLeadForm({
      name: lead.name || '',
      phoneNumber: lead.phoneNumber || '',
      comments: lead.comments || '',
    });
    setLeadModalOpen(true);
  };

  const handleSaveLead = async () => {
    const name = leadForm.name.trim();
    const phoneNumber = leadForm.phoneNumber.trim();
    if (!name || !phoneNumber) {
      toast.error('Name and phone number are required');
      return;
    }
    try {
      setLeadSaving(true);
      if (editingLead?.id) {
        await tenantService.updateLead(editingLead.id, {
          name,
          phoneNumber,
          comments: leadForm.comments.trim(),
        });
        toast.success('Lead updated');
      } else {
        await tenantService.createLead(hostelId, {
          name,
          phoneNumber,
          comments: leadForm.comments.trim(),
        });
        toast.success('Lead added');
      }
      setLeadModalOpen(false);
      setEditingLead(null);
      await loadLeads();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not save lead';
      toast.error(msg);
    } finally {
      setLeadSaving(false);
    }
  };

  const handleDeleteLead = async (lead: TenantLead) => {
    const ok = window.confirm(`Delete lead for ${lead.name}?`);
    if (!ok) return;
    try {
      await tenantService.deleteLead(lead.id);
      toast.success('Lead deleted');
      await loadLeads();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="leads-panel">
      <div className="leads-panel-toolbar">
        <button type="button" className="tenants-dash-btn tenants-dash-btn--primary" onClick={openAddLead}>
          + Add lead
        </button>
        <label className="leads-panel-search">
          <span aria-hidden>🔍</span>
          <input
            type="search"
            placeholder="Search name, phone, comments…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search leads"
          />
        </label>
      </div>

      <section className="leads-panel-card">
        {loading ? (
          <p className="leads-panel-empty">Loading leads…</p>
        ) : (
          <>
            <div className="leads-panel-meta">
              <span>
                <strong>{filtered.length}</strong> lead{filtered.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="leads-panel-table-wrap">
              <table className="leads-panel-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Comments</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="leads-panel-no-data">
                        No leads yet. Click &quot;Add lead&quot; to capture name, phone, and comments.
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((lead) => (
                      <tr key={lead.id}>
                        <td className="leads-panel-name">{lead.name}</td>
                        <td>
                          <PhoneLink phone={lead.phoneNumber} className="leads-panel-phone" />
                        </td>
                        <td className="leads-panel-comments" title={lead.comments || undefined}>
                          {lead.comments?.trim() ? lead.comments : '—'}
                        </td>
                        <td>{formatLeadDate(lead)}</td>
                        <td>
                          <div className="leads-panel-actions">
                            <button type="button" className="leads-panel-btn leads-panel-btn--edit" onClick={() => openEditLead(lead)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="leads-panel-btn leads-panel-btn--delete"
                              onClick={() => void handleDeleteLead(lead)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <footer className="leads-panel-footer">
              <div className="leads-panel-pagination">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  ‹ Prev
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next ›
                </button>
              </div>
              <label>
                Rows
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  <option value={8}>8</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </footer>
          </>
        )}
      </section>

      {leadModalOpen && (
        <div
          className="leads-modal-overlay"
          role="presentation"
          onClick={() => !leadSaving && setLeadModalOpen(false)}
        >
          <div className="leads-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="leads-modal-close"
              onClick={() => setLeadModalOpen(false)}
              disabled={leadSaving}
              aria-label="Close"
            >
              ×
            </button>
            <h2>{editingLead ? 'Edit lead' : 'Add lead'}</h2>
            <p className="leads-modal-sub">Name, phone number, and comments for follow-up.</p>
            <div className="leads-modal-form">
              <label>
                Name *
                <input
                  type="text"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Full name"
                  autoFocus
                />
              </label>
              <label>
                Phone number *
                <input
                  type="tel"
                  value={leadForm.phoneNumber}
                  onChange={(e) => setLeadForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                  placeholder="10-digit mobile"
                />
              </label>
              <label>
                Comments
                <textarea
                  rows={4}
                  value={leadForm.comments}
                  onChange={(e) => setLeadForm((f) => ({ ...f, comments: e.target.value }))}
                  placeholder="Follow-up notes, preferred room, visit date…"
                />
              </label>
              <div className="leads-modal-actions">
                <button type="button" className="btn" onClick={() => setLeadModalOpen(false)} disabled={leadSaving}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={leadSaving} onClick={() => void handleSaveLead()}>
                  {leadSaving ? 'Saving…' : editingLead ? 'Save changes' : 'Add lead'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPanel;
